import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteForm } from 'api/Forms';

import { deletedMsg, failedMsg } from 'lib/constants/messages';
import { useFormBuilderContext } from 'lib/context/FormBuilder/FormBuilderContext';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import DuplicateForm from '../../forms/DuplicateForm';

import ViewForm from './ViewForm';

const Actions = ({ data }) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formToView, setFormToView] = useState({});
  const [viewFormModal, setViewFormModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const { setOpenModal, setBreadCrumbs, formik, setIsEdit, practiceId } = useFormBuilderContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDeleteForm } = useMutation({
    mutationFn: (data) => deleteForm(navigate, data)
  });

  const handleEdit = () => {
    setBreadCrumbs([{ title: 'Form builder', view: 'builder', active: true, isFinished: false }]);
    formik.setValues({ ...formik.values, form: data });
    setIsEdit(true);
    setOpenModal(true);
  };

  const handleDelete = async () => {
    const variables = { formId: data?.id, practice_id: practiceId };
    await mutateDeleteForm(variables, {
      onSuccess: ({ code }) => {
        if (code === 0) {
          queryClient.refetchQueries(['forms']);
          queryClient.refetchQueries(['customForm']);
          showAlert({ title: deletedMsg('Form'), color: 'success' });
        } else {
          showAlert({ title: failedMsg('delete', 'the form'), color: 'danger' });
        }
        setShowConfirmationModal(false);
      }
    });
  };

  const handleView = () => {
    setFormToView(data);
    setViewFormModal(true);
  };

  const options = [
    { label: 'View', onClick: handleView, icon: 'eye-mono' },
    { label: 'Edit', onClick: handleEdit, icon: 'edit-2-mono' },
    { label: 'Delete', onClick: () => setShowConfirmationModal(true), icon: 'trash-mono' },
    {
      label: 'Duplicate',
      onClick: () => {
        setSelectedForm(data);
        setShowDuplicateModal(true);
      },
      icon: 'copy-linear'
    }
  ];
  return (
    <>
      <Popover
        className="flex h-full items-center"
        position="left"
        isFixed
        isBorder={false}
        options={options}
      />
      {showConfirmationModal && (
        <Confirm
          variant="danger"
          title="Delete form"
          icon="trash-mono"
          message="Are you sure you want to delete this form?"
          handleOpen={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: handleDelete,
              color: 'danger'
            }
          }}
        />
      )}
      {viewFormModal && (
        <ViewForm
          form={formToView}
          setViewFormModal={setViewFormModal}
          viewFormModal={viewFormModal}
        />
      )}
      {!!showDuplicateModal && (
        <DuplicateForm
          showDuplicateModal={showDuplicateModal}
          setShowDuplicateModal={setShowDuplicateModal}
          formId={selectedForm.id}
          formVersion={selectedForm.version}
        />
      )}
    </>
  );
};

export default Actions;
